import { ReactNode, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useDemoMode, DEMO_MODE_AVAILABLE } from '@/contexts/DemoContext';
import { useClinicBranding } from '@/hooks/useClinicBranding';
import { api } from '@/lib/api';
import { SUPPORTED_LANGUAGES } from '@/i18n';
import { Sidebar } from './Sidebar';
import { GlobalProgressBar } from './GlobalProgressBar';
import { TrialBanner } from './TrialBanner';

interface NavItem {
  to: string;
  labelKey: string;
  icon: JSX.Element;
  exact?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  {
    to: '/dashboard',
    labelKey: 'nav.dashboard',
    exact: true,
    icon: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    to: '/patients',
    labelKey: 'nav.patients',
    icon: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    to: '/analytics',
    labelKey: 'nav.analytics',
    icon: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
];

interface Props {
  children: ReactNode;
}

export function AppLayout({ children }: Props): JSX.Element {
  const { user, logout, sessionWarning, dismissSessionWarning } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const branding = useClinicBranding();
  const [adminOpen, setAdminOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  // ── First-login gate: redirect to onboarding if not complete ──
  const onboardingChecked = useRef(false);
  useEffect(() => {
    if (onboardingChecked.current || !user) return;
    onboardingChecked.current = true;
    api.get<Record<string, unknown>>('/clinics/settings')
      .then(res => {
        if (res.success && res.data && !res.data.onboardingComplete) {
          navigate('/onboarding', { replace: true });
        }
      })
      .catch(() => { /* ignore — don't block app */ });
  }, [user, navigate]);

  const isActive = (item: NavItem): boolean => {
    if (item.exact) return location.pathname === item.to;
    return location.pathname.startsWith(item.to);
  };

  const handleLogout = (): void => {
    logout();
    navigate('/login', { replace: true });
  };

  const displayName = user
    ? [user.title, user.firstName, user.lastName].filter(Boolean).join(' ')
    : 'Clinician';
  // Initials use firstName + lastName only (not title)
  const initials = user
    ? `${user.firstName[0] ?? ''}${user.lastName[0] ?? ''}`.toUpperCase()
    : '?';
  const roleLabel: Record<string, string> = {
    super_admin: 'Super Admin',
    clinic_admin: 'Admin',
    clinician: 'Clinician',
    front_desk: 'Front Desk',
  };

  const { isDemoMode, setDemoMode } = useDemoMode();

  return (
    <div className="min-h-screen bg-surface-main flex">
      {/* ── Global API progress bar (FIX-20) ── */}
      <GlobalProgressBar />

      {/* ── Session timeout warning modal ── */}
      {sessionWarning && createPortal(
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '28px 32px',
            maxWidth: '400px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>&#9200;</div>
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#2c2c2a', marginBottom: '8px' }}>
              {t('auth.sessionExpiring', 'Session Expiring Soon')}
            </h3>
            <p style={{ fontSize: '13px', color: '#6b665e', marginBottom: '20px' }}>
              {t('session.warning.message', 'Your session will expire in 5 minutes. Click to stay logged in.')}
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button onClick={dismissSessionWarning} className="session-expiry-warning" style={{
                padding: '8px 20px', background: '#2c2c2a', color: '#fff',
                border: 'none', borderRadius: '7px', fontSize: '13px',
                fontWeight: 500, cursor: 'pointer',
              }}>
                {t('session.warning.button', 'Stay logged in')}
              </button>
              <button onClick={handleLogout} style={{
                padding: '8px 20px', background: '#fff', color: '#6b665e',
                border: '0.5px solid #d6cfc4', borderRadius: '7px', fontSize: '13px',
                cursor: 'pointer',
              }}>
                {t('auth.logoutNow', 'Log Out Now')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Demo Mode Banner ── */}
      {DEMO_MODE_AVAILABLE && isDemoMode && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white text-center py-1.5 text-xs font-medium flex items-center justify-center gap-3">
          <span>🎭 Demo Mode — Sample data shown</span>
          <button onClick={() => setDemoMode(false)} className="px-3 py-0.5 bg-white/20 rounded hover:bg-white/30 text-xs">Exit Demo</button>
        </div>
      )}

      {/* ── Sidebar ─────────────────────────────────────────── */}
      <Sidebar />

      {/* ── Main Content ─────────────────────────────────────── */}
      <div className={`flex-1 flex flex-col min-h-screen overflow-x-hidden ${isDemoMode ? 'mt-8' : ''}`}>
        {/* ── Trial / billing banner ── */}
        <TrialBanner />

        {/* Compact top bar — HIDDEN on mobile (sidebar has controls) */}
        <header className="hidden md:block sticky top-0 z-20 bg-surface-main/95 backdrop-blur-sm border-b border-charcoal-100">
          <div className="px-5 h-11 flex items-center justify-end gap-3">
            {/* Language selector */}
            <div className="relative">
              <button onClick={() => setLangOpen(!langOpen)} title="Language"
                className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-[11px] text-charcoal-500 hover:bg-surface-hover">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                <span className="uppercase">{i18n.language}</span>
              </button>
              {langOpen && createPortal(
                <div className="fixed inset-0 z-50" onClick={() => setLangOpen(false)}>
                  <div className="absolute right-4 top-12 bg-white rounded-lg shadow-md border border-charcoal-200 py-1 min-w-[120px]" onClick={e => e.stopPropagation()}>
                    {SUPPORTED_LANGUAGES.map(l => (
                      <button key={l.code} onClick={() => { i18n.changeLanguage(l.code); setLangOpen(false); }}
                        className={`w-full text-left px-3 py-1.5 text-[12px] hover:bg-surface-hover ${i18n.language === l.code ? 'font-medium text-charcoal-900' : 'text-charcoal-600'}`}>
                        {l.name}
                      </button>
                    ))}
                  </div>
                </div>, document.body
              )}
            </div>

            {/* User avatar */}
            <button onClick={() => setAdminOpen(!adminOpen)}
              className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-surface-hover transition-colors">
              <div className="w-6 h-6 rounded-full bg-charcoal-900 flex items-center justify-center">
                <span className="text-[8px] text-charcoal-50 font-medium">{initials}</span>
              </div>
            </button>
            {adminOpen && createPortal(
              <div className="fixed inset-0 z-50" onClick={() => setAdminOpen(false)}>
                <div className="absolute right-4 top-12 bg-white rounded-lg shadow-md border border-charcoal-200 py-1 min-w-[180px]" onClick={e => e.stopPropagation()}>
                  <div className="px-3 py-2 border-b border-charcoal-100">
                    <p className="text-[12px] font-medium text-charcoal-900">{displayName}</p>
                    <p className="text-[10px] text-charcoal-500">{user?.email}</p>
                  </div>
                  <button onClick={() => { navigate('/settings/clinic'); setAdminOpen(false); }}
                    className="w-full text-left px-3 py-1.5 text-[12px] text-charcoal-600 hover:bg-surface-hover">Settings</button>
                  <button onClick={handleLogout}
                    className="w-full text-left px-3 py-1.5 text-[12px] text-red-600 hover:bg-red-50">Sign out</button>
                </div>
              </div>, document.body
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 px-5 py-5 lg:px-7">
          {children}
        </main>
      </div>

    </div>
  );

}
